import React, { useRef, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { DatePickerFr } from '../ui/DatePickerFr';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { HardHat, Plus, Trash2, ChevronRight, Info, Shield, Building, Upload, FileText, X, CheckCircle, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import SiretInput from '../ui/SiretInput';

function ProgressBar({ progress }) {
  return (
    <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mt-2">
      <div
        className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full transition-all duration-300"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}

function DocPreviewDialog({ open, onClose, previewUrl, filename, contentType }) {
  const isPdf = contentType?.includes('pdf');
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <FileText className="h-4 w-4" />
            {filename}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-[60vh] overflow-auto rounded-lg border bg-gray-50">
          {isPdf ? (
            <iframe src={previewUrl} className="w-full h-[70vh] rounded-lg" title={filename} />
          ) : (
            <img src={previewUrl} alt={filename} className="max-w-full max-h-[70vh] mx-auto object-contain p-4" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function IntervenantDocUpload({ intervenant, index, reference, headers, intervenantsDocuments, onDocUploaded, onAutoSave }) {
  const devisRef = useRef(null);
  const attestRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [previewState, setPreviewState] = useState({ open: false, url: '', filename: '', contentType: '' });

  const intId = String(intervenant.id);
  const docs = intervenantsDocuments?.[intId] || {};
  const devisDoc = docs.devis;
  const attestDoc = docs.attestation_assurance;

  const handleUpload = useCallback(async (file, docType) => {
    let workingReference = reference;
    // Auto-save de la simulation si pas encore de référence : permet l'upload
    // de justificatifs dès l'étape souscription, sans étape manuelle préalable
    if (!workingReference) {
      if (typeof onAutoSave === 'function') {
        try {
          toast.info('Création automatique de la simulation pour permettre l\'upload...');
          const saved = await onAutoSave({ silent: true });
          workingReference = saved?.reference || saved?.simulation_id;
        } catch {
          // fallthrough
        }
      }
      if (!workingReference) {
        toast.error('Impossible d\'enregistrer la simulation. Vérifiez les champs obligatoires.');
        return;
      }
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('intervenant_id', intId);
    formData.append('doc_type', docType);

    setUploadProgress(prev => ({ ...prev, [docType]: 0 }));

    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const xhr = new XMLHttpRequest();

      const result = await new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(prev => ({ ...prev, [docType]: pct }));
          }
        });
        xhr.addEventListener('load', () => {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch { reject(new Error('Réponse invalide')); }
        });
        xhr.addEventListener('error', () => reject(new Error('Erreur réseau')));
        xhr.open('POST', `${API}/api/domium/subscribe/${workingReference}/intervenant-document`);
        xhr.setRequestHeader('Authorization', headers?.Authorization || '');
        xhr.send(formData);
      });

      if (result.success) {
        toast.success(`${docType === 'devis' ? 'Devis' : "Attestation d'assurance"} telecharge`);
        onDocUploaded?.(intId, docType, { filename: file.name, size: file.size, content_type: file.type, uploaded_at: new Date().toISOString() });
      } else {
        toast.error(result.detail || 'Erreur lors du telechargement');
      }
    } catch {
      toast.error('Erreur reseau');
    } finally {
      setTimeout(() => setUploadProgress(prev => { const n = { ...prev }; delete n[docType]; return n; }), 600);
    }
  }, [reference, intId, headers, onDocUploaded, onAutoSave]);

  const handleDelete = useCallback(async (docType) => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/domium/subscribe/${reference}/intervenant-document/${intId}/${docType}`, {
        method: 'DELETE',
        headers: { Authorization: headers?.Authorization || '', 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Document supprime');
        onDocUploaded?.(intId, docType, null);
      }
    } catch {
      toast.error('Erreur');
    }
  }, [reference, intId, headers, onDocUploaded]);

  const handlePreview = useCallback((docType, docData) => {
    const API = process.env.REACT_APP_BACKEND_URL;
    const url = `${API}/api/domium/subscribe/${reference}/intervenant-document/${intId}/${docType}?token=${encodeURIComponent(headers?.Authorization?.replace('Bearer ', '') || '')}`;
    setPreviewState({ open: true, url, filename: docData.filename, contentType: docData.content_type });
  }, [reference, intId, headers]);

  const renderDocSlot = (docType, label, docData, inputRef) => {
    const progress = uploadProgress[docType];
    const uploading = progress !== undefined;

    return (
      <div className="flex-1 p-3 rounded-lg border-2 border-dashed border-gray-200 hover:border-blue-300 transition-colors bg-white">
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png,.webp"
          onChange={(e) => { if (e.target.files?.[0]) handleUpload(e.target.files[0], docType); e.target.value = ''; }}
          data-testid={`upload-${docType}-${index}`}
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-1 py-1">
            <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
            <span className="text-xs font-medium text-blue-600">{progress}%</span>
            <ProgressBar progress={progress} />
          </div>
        ) : docData ? (
          <div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-emerald-700 truncate" data-testid={`doc-name-${docType}-${index}`}>{docData.filename}</p>
                <p className="text-[10px] text-gray-400">{label} - {docData.size ? `${(docData.size / 1024).toFixed(0)} Ko` : ''}</p>
              </div>
              <div className="flex gap-0.5 shrink-0">
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-blue-400 hover:text-blue-600" onClick={() => handlePreview(docType, docData)} data-testid={`preview-${docType}-${index}`}>
                  <Eye className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => handleDelete(docType)} data-testid={`delete-${docType}-${index}`}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="mt-1 text-[10px] text-blue-500 hover:underline cursor-pointer"
            >
              Remplacer le fichier
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-full flex flex-col items-center gap-1 py-1 text-gray-400 hover:text-blue-500 transition-colors cursor-pointer"
            data-testid={`btn-upload-${docType}-${index}`}
          >
            <Upload className="h-4 w-4" />
            <span className="text-xs font-medium">{label}</span>
            <span className="text-[10px]">PDF, JPEG, PNG (max 10 Mo)</span>
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="mt-3 space-y-2">
        <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
          <FileText className="h-3.5 w-3.5" />
          Pieces justificatives
        </h5>
        <div className="flex gap-3">
          {renderDocSlot('devis', 'Devis', devisDoc, devisRef)}
          {renderDocSlot('attestation_assurance', "Attestation d'assurance", attestDoc, attestRef)}
        </div>
      </div>
      <DocPreviewDialog
        open={previewState.open}
        onClose={() => setPreviewState(p => ({ ...p, open: false }))}
        previewUrl={previewState.url}
        filename={previewState.filename}
        contentType={previewState.contentType}
      />
    </>
  );
}

export default function IntervenantsTab({ 
  intervenants, setIntervenants: setIntervenants, addIntervenant, removeIntervenant, updateIntervenant, setActiveTab,
  reference, headers, intervenantsDocuments, onDocUploaded, onAutoSave
}) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <HardHat className="h-5 w-5 text-emerald-600" />
            Entreprises intervenantes a l'operation
          </CardTitle>
          <CardDescription>
            Renseignez les informations des entreprises participant aux travaux de construction
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {intervenants.map((intervenant, index) => (
            <div key={intervenant.id} className="border rounded-lg p-4 bg-gray-50 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  Entreprise {index + 1}
                </h4>
                {intervenants.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => removeIntervenant(intervenant.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Raison sociale *</Label>
                  <Input value={intervenant.raison_sociale} onChange={(e) => updateIntervenant(intervenant.id, 'raison_sociale', e.target.value)} placeholder="Nom de l'entreprise" className="mt-1" data-testid={`intervenant-${index}-raison-sociale`} />
                </div>
                <div>
                  <Label>SIRET *</Label>
                  <SiretInput
                    value={intervenant.siret || ''}
                    onChange={(value) => updateIntervenant(intervenant.id, 'siret', (value || '').replace(/\D/g, '').slice(0, 14))}
                    onAutoComplete={(company) => {
                      // Complète raison sociale uniquement si vide
                      if (!intervenant.raison_sociale && company?.raison_sociale) {
                        updateIntervenant(intervenant.id, 'raison_sociale', company.raison_sociale);
                      }
                    }}
                    showLabel={false}
                    placeholder="14 chiffres — auto-complétion"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Role / Corps de metier</Label>
                  <Select value={intervenant.role} onValueChange={(v) => updateIntervenant(intervenant.id, 'role', v)}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selectionner le role" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="entreprise_generale">Entreprise generale</SelectItem>
                      <SelectItem value="gros_oeuvre">Gros oeuvre</SelectItem>
                      <SelectItem value="charpente_couverture">Charpente / Couverture</SelectItem>
                      <SelectItem value="electricite">Electricite</SelectItem>
                      <SelectItem value="plomberie">Plomberie</SelectItem>
                      <SelectItem value="menuiserie">Menuiserie</SelectItem>
                      <SelectItem value="carrelage">Carrelage</SelectItem>
                      <SelectItem value="peinture">Peinture</SelectItem>
                      <SelectItem value="climatisation">Climatisation / CVC</SelectItem>
                      <SelectItem value="terrassement">Terrassement / VRD</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Separator />
              <div>
                <h5 className="font-medium text-gray-600 mb-3 flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Assurance decennale de l'entreprise
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Compagnie d'assurance *</Label>
                    <Input value={intervenant.assurance_compagnie} onChange={(e) => updateIntervenant(intervenant.id, 'assurance_compagnie', e.target.value)} placeholder="Nom de la compagnie" className="mt-1" data-testid={`intervenant-${index}-assurance-compagnie`} />
                  </div>
                  <div>
                    <Label>Date d'effet de l'attestation *</Label>
                    <DatePickerFr value={intervenant.assurance_date_effet} onChange={(v) => updateIntervenant(intervenant.id, 'assurance_date_effet', v)} className="mt-1 w-full" id={`intervenant-${index}-assurance-date`} />
                  </div>
                  <div>
                    <Label>N de police</Label>
                    <Input value={intervenant.assurance_numero_police} onChange={(e) => updateIntervenant(intervenant.id, 'assurance_numero_police', e.target.value)} placeholder="Numero de police d'assurance" className="mt-1" data-testid={`intervenant-${index}-assurance-police`} />
                  </div>
                  <div>
                    <Label>Ref. solidaire (si applicable)</Label>
                    <Input value={intervenant.assurance_reference_solidaire} onChange={(e) => updateIntervenant(intervenant.id, 'assurance_reference_solidaire', e.target.value)} placeholder="Reference dispositif solidaire" className="mt-1" data-testid={`intervenant-${index}-assurance-ref-solidaire`} />
                  </div>
                </div>
              </div>
              <Separator />
              <IntervenantDocUpload
                intervenant={intervenant}
                index={index}
                reference={reference}
                headers={headers}
                intervenantsDocuments={intervenantsDocuments}
                onDocUploaded={onDocUploaded}
                onAutoSave={onAutoSave}
              />
            </div>
          ))}
          <Button type="button" variant="outline" onClick={addIntervenant} className="w-full border-dashed border-2">
            <Plus className="h-4 w-4 mr-2" />
            Ajouter une entreprise intervenante
          </Button>
          <Alert className="bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>Pieces justificatives requises :</strong> Vous devrez fournir les attestations d'assurance 
              decennale en cours de validite et les devis pour chaque entreprise intervenante.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setActiveTab('operation')}>
          <ChevronRight className="h-4 w-4 mr-2 rotate-180" />
          Retour
        </Button>
        <Button onClick={() => setActiveTab('technique')} className="bg-emerald-600 hover:bg-emerald-700">
          Suivant : Technique
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
